import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { ReactComponent as CopySvg } from '@assets/icons/copy.svg';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { ReactComponent as UnlockSvg } from '@assets/icons/unlock.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as SettingsSvg } from '@assets/icons/dropdown/settings-linear.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as ExternalLinkSvg } from '@assets/icons/external-link.svg';

import { Table, Tooltip, Dropdown, Switch, message, Button } from 'antd';

import { ActionToast } from '@components/Toasts';

import { AssetReviewItemDto } from '@api/Api';
import removeHttp from '@helpers/removeHttps';
import moment from 'moment';
import { toggleShareAssetModal } from '@redux/actions/modalAction';
import { useDispatch } from 'react-redux';
import {
  useDeleteAssetReviewLinkMutation,
  useUpdateAssetReviewMutation
} from '@hooks/queries/assetReview';
import classNames from 'classnames';

import './AssetLinksTable.less';
import { Link } from 'react-router-dom';
import LottieComponent from '@components/Lottie';

type AssetLinksTableProps = {
  linksData: { node: AssetReviewItemDto; cursor: string }[];
  loading: boolean;
  campaignId: string;
};

function AssetLinksTable({
  linksData,
  loading,
  campaignId
}: AssetLinksTableProps) {
  const dispatch = useDispatch();
  const [copiedLink, setCopiedLink] = useState<string>();
  const [switchLoading, setSwitchLoading] = useState<string | boolean>(false);

  const { mutateAsync: updateAssetReview } = useUpdateAssetReviewMutation({});
  const { mutateAsync: deleteAssetReviewLink } =
    useDeleteAssetReviewLinkMutation({});

  const onActiveChange = async (v: boolean, id: string) => {
    setSwitchLoading(id);
    await updateAssetReview({
      reviewId: id,
      active: v
    });
    setSwitchLoading(false);
  };

  const onRemoveLink = (id: string) => {
    deleteAssetReviewLink({ reviewId: id });
  };

  const removeLink = (id: string) => {
    let action = true;
    toast(
      <ActionToast
        title="Link removed"
        onUndo={() => {
          action = false;
        }}
        description={undefined}
        closeToast={undefined}
      />,
      {
        onClose: () => action && onRemoveLink(id)
      }
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 250,
      render: (_name: string, { node }: { node: AssetReviewItemDto }) => {
        return (
          <Tooltip
            placement="top"
            title={node.name}
            overlayClassName="toggle_favorite_overlay toggle_favorite_overlay"
          >
            <Link
              to={`/campaigns/reviews/${campaignId}/${node.id}`}
              className="share-name"
            >
              <LinkSvg />
              <span className="share-name-text">{node.name}</span>
            </Link>
          </Tooltip>
        );
      }
    },
    {
      title: 'Link',
      dataIndex: 'link',
      width: 212,
      render: (text: string, { node }: { node: AssetReviewItemDto }) => {
        return (
          <div className="link_name">
            <div
              className={classNames({
                link: true,
                'link--copied': copiedLink === node.url
              })}
              onClick={(e) => {
                message.success({
                  content: 'Copied link to clipboard',
                  className: 'message-dark-modal',
                  icon: <SuccessSvg className="anticon" />
                });
                setCopiedLink(node.url);
                navigator.clipboard.writeText(node.url);
              }}
            >
              <span className="main-body-text">{removeHttp(node.url)}</span>
              <CopySvg />
            </div>
            <div className="access">
              <Tooltip
                placement="bottomLeft"
                overlayClassName="toggle_favorite_overlay"
                title={node.password ? 'Password enabled' : 'Password disabled'}
              >
                {node.password ? <LockSvg /> : <UnlockSvg />}
              </Tooltip>
              {(node.allowComments || node.allowDownloads) && (
                <span>
                  Allow: {node.allowComments && 'comments'}
                  {node.allowDownloads && node.allowComments && ', '}
                  {node.allowDownloads && 'downloads'}
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Date created',
      dataIndex: 'dateCreated',
      width: 166,
      render: (text: string, { node }: { node: AssetReviewItemDto }) => (
        <>{moment(node.createdAt).format('MMM DD, h:mm a')}</>
      )
    },
    {
      title: 'Viewed',
      dataIndex: 'viewed',
      width: 84,
      render: (text: string, { node }: { node: AssetReviewItemDto }) =>
        node.visitsCounter
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 115,
      render: (text: string, { node }: { node: AssetReviewItemDto }) => {
        const copy = node.active ? 'On' : 'Off';
        return (
          <>
            <Switch
              loading={switchLoading === node.id}
              onChange={(v) => onActiveChange(v, node.id)}
              checked={node.active}
              style={{ marginRight: 12 }}
            />{' '}
            {copy}
          </>
        );
      }
    },
    {
      title: '',
      key: 'action',
      width: 30,
      render: (text: string, { node }: { node: AssetReviewItemDto }) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'linkSettings',
                  label: 'Link settings',
                  icon: <SettingsSvg />,
                  onClick: () => {
                    dispatch(
                      toggleShareAssetModal({
                        visible: true,
                        reviewId: node.id,
                        defaultActiveTab: 'settings'
                      })
                    );
                  }
                },
                {
                  key: 'renameLink',
                  label: 'Rename',
                  icon: <RenameSvg />,
                  onClick: () => {
                    dispatch(
                      toggleShareAssetModal({
                        visible: true,
                        reviewId: node.id
                      })
                    );
                  }
                },
                {
                  type: 'divider'
                },
                {
                  key: 'copyLink',
                  label: 'Copy link',
                  icon: <LinkSvg />,
                  onClick: () => {
                    message.info('Copied to clipboard');
                    navigator.clipboard.writeText(node.url);
                  }
                },
                {
                  key: 'previewInNewTab',
                  label: 'Preview in new tab',
                  icon: <ExternalLinkSvg className="b-opacity-icon" />,
                  onClick: () => {
                    window.open(node.url, '_blank');
                  }
                },
                {
                  type: 'divider'
                },
                {
                  key: 'removeLink',
                  label: 'Delete',
                  icon: <DeleteSvg className="b-opacity-icon" />,
                  onClick: () => removeLink(node.id)
                }
              ]
            }}
            overlayClassName="asset_links_overlay"
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              onClick={(e) => e.stopPropagation()}
              type="text"
              size="small"
              style={{ padding: 0, width: 24, height: 24 }}
            >
              <ActionSvg style={{ marginRight: 0 }} />
            </Button>
          </Dropdown>
        );
      }
    }
  ];

  return (
    <div className="asset-links-table">
      <Table
        columns={columns}
        loading={loading}
        dataSource={linksData}
        pagination={false}
        rowKey={({ node }: { node: AssetReviewItemDto }) => node?.id}
        locale={{
          emptyText: (
            <>
              {!linksData.length && (
                <div className="empty_state empty_state--search">
                  <LottieComponent className="icon" view="noSearchingResult" />
                  <div
                    className="main-body-text main-body-text--bold"
                    style={{ marginBottom: 8 }}
                  >
                    No results found
                  </div>
                  <p className="copy main-body-text main-body-text--dark-gray">
                    It seems we can’t find any results based on your search
                  </p>
                </div>
              )}
            </>
          )
        }}
      />
    </div>
  );
}

export default AssetLinksTable;
